import React, { useState, useMemo } from 'react';
import type { ElevationPoint, SpiritedSegment } from '../types';
import { getSectorProfileRange } from '../utils/raceSector';

interface GroupRideElevationChartProps {
    elevationProfile?: ElevationPoint[];
    spiritedSegment?: SpiritedSegment;
    elevationGainFt?: number;
}

const GroupRideElevationChart: React.FC<GroupRideElevationChartProps> = ({
    elevationProfile = [],
    spiritedSegment,
    elevationGainFt
}) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    // Compute metrics and bounding box for SVG chart
    const stats = useMemo(() => {
        if (!elevationProfile || elevationProfile.length === 0) {
            return null;
        }

        let minEle = Infinity;
        let maxEle = -Infinity;
        let totalGain = 0;

        for (let i = 0; i < elevationProfile.length; i++) {
            const pt = elevationProfile[i];
            if (pt.elevationFt < minEle) minEle = pt.elevationFt;
            if (pt.elevationFt > maxEle) maxEle = pt.elevationFt;

            if (i > 0) {
                const prev = elevationProfile[i - 1];
                const diff = pt.elevationFt - prev.elevationFt;
                if (diff > 0.1) totalGain += diff;
            }
        }

        const totalDist = elevationProfile[elevationProfile.length - 1].distanceMiles;
        const eleRange = Math.max(50, maxEle - minEle);

        return {
            minEle: Math.floor(minEle),
            maxEle: Math.ceil(maxEle),
            totalGain: elevationGainFt ?? Math.round(totalGain),
            totalDist: Number(totalDist.toFixed(1)),
            eleRange
        };
    }, [elevationProfile, elevationGainFt]);

    if (!stats || elevationProfile.length < 2) {
        return null;
    }

    // Chart dimensions
    const width = 800;
    const height = 180;
    const padding = { top: 20, right: 30, bottom: 30, left: 50 };
    const chartW = width - padding.left - padding.right;
    const chartH = height - padding.top - padding.bottom;

    // Convert data points to SVG coordinates
    const points = elevationProfile.map((pt, idx) => {
        const x = padding.left + (pt.distanceMiles / stats.totalDist) * chartW;
        const y = padding.top + chartH - ((pt.elevationFt - stats.minEle) / stats.eleRange) * chartH;
        return { x, y, pt, idx };
    });

    const pathD = points.reduce((acc, p, i) => {
        return i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`;
    }, '');

    const areaD = `${pathD} L ${points[points.length - 1].x} ${padding.top + chartH} L ${points[0].x} ${padding.top + chartH} Z`;

    // Race Sector sub-path (highlighted in cyan)
    const sectorRange = getSectorProfileRange(elevationProfile, spiritedSegment);
    const sectorPoints = sectorRange ? points.slice(sectorRange.startIdx, sectorRange.endIdx + 1) : [];
    const sectorPathD = sectorPoints.reduce((acc, p, i) => {
        return i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`;
    }, '');
    const sectorAreaD = sectorPoints.length > 1
        ? `${sectorPathD} L ${sectorPoints[sectorPoints.length - 1].x} ${padding.top + chartH} L ${sectorPoints[0].x} ${padding.top + chartH} Z`
        : '';
    const sectorStartX = sectorPoints.length > 1 ? sectorPoints[0].x : 0;
    const sectorEndX = sectorPoints.length > 1 ? sectorPoints[sectorPoints.length - 1].x : 0;
    const isHoverInSector = hoverIndex !== null && sectorRange !== null && hoverIndex >= sectorRange.startIdx && hoverIndex <= sectorRange.endIdx;

    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const scaleX = width / rect.width;
        const svgX = mouseX * scaleX;

        // Find nearest point
        let nearestIdx = 0;
        let minDiff = Infinity;
        for (let i = 0; i < points.length; i++) {
            const diff = Math.abs(points[i].x - svgX);
            if (diff < minDiff) {
                minDiff = diff;
                nearestIdx = i;
            }
        }
        setHoverIndex(nearestIdx);
    };

    const hoverPt = hoverIndex !== null ? points[hoverIndex] : null;

    // Grade calculation for hovered point
    let currentGrade = 0;
    if (hoverIndex !== null && hoverIndex > 0 && hoverIndex < points.length) {
        const prevPt = elevationProfile[hoverIndex - 1];
        const curPt = elevationProfile[hoverIndex];
        const distDiffFt = (curPt.distanceMiles - prevPt.distanceMiles) * 5280;
        const eleDiffFt = curPt.elevationFt - prevPt.elevationFt;
        if (distDiffFt > 0) {
            currentGrade = Number(((eleDiffFt / distDiffFt) * 100).toFixed(1));
        }
    }

    return (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 sm:p-5 shadow-xl space-y-4">
            {/* Header & Metrics */}
            <div className="flex flex-wrap items-center justify-between gap-y-3 gap-x-2 border-b border-gray-700 pb-3">
                <div className="flex items-center gap-3">
                    <h3 className="text-base font-bold text-cyan-400 flex items-center gap-2">
                        <span>Elevation Profile</span>
                    </h3>
                    {sectorAreaD && (
                        <span className="flex items-center gap-1.5 text-xs font-medium text-gray-400">
                            <span className="w-2.5 h-2.5 rounded-full bg-cyan-500"></span> Race Sector
                        </span>
                    )}
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-1.5 sm:gap-3 md:gap-4 text-[11px] sm:text-xs font-semibold w-full sm:w-auto">
                    <div className="flex items-center gap-1 sm:gap-1.5 bg-gray-900/80 px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg border border-gray-700 whitespace-nowrap">
                        <span className="text-gray-400">Distance:</span>
                        <span className="text-cyan-400 font-bold">{stats.totalDist} mi</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-1.5 bg-gray-900/80 px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg border border-gray-700 whitespace-nowrap">
                        <span className="text-gray-400">Elev Gain:</span>
                        <span className="text-emerald-400 font-bold">+{stats.totalGain.toLocaleString()} ft</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-1.5 bg-gray-900/80 px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg border border-gray-700 whitespace-nowrap">
                        <span className="text-gray-400">Peak:</span>
                        <span className="text-amber-400 font-bold">{stats.maxEle.toLocaleString()} ft</span>
                    </div>
                </div>
            </div>

            {/* SVG Profile Chart */}
            <div className="relative w-full overflow-hidden">
                <svg
                    viewBox={`0 0 ${width} ${height}`}
                    className="w-full h-auto cursor-crosshair"
                    onMouseMove={handleMouseMove}
                    onMouseLeave={() => setHoverIndex(null)}
                >
                    <defs>
                        <linearGradient id="elevationGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.35" />
                            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.0" />
                        </linearGradient>
                        <linearGradient id="sectorGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.55" />
                            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.05" />
                        </linearGradient>
                    </defs>

                    {/* Gridlines */}
                    <line x1={padding.left} y1={padding.top} x2={width - padding.right} y2={padding.top} stroke="#374151" strokeDasharray="3 3" />
                    <line x1={padding.left} y1={padding.top + chartH / 2} x2={width - padding.right} y2={padding.top + chartH / 2} stroke="#374151" strokeDasharray="3 3" />
                    <line x1={padding.left} y1={padding.top + chartH} x2={width - padding.right} y2={padding.top + chartH} stroke="#4b5563" />

                    {/* Area fill & Contour line */}
                    <path d={areaD} fill="url(#elevationGrad)" />
                    <path d={pathD} fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

                    {/* Race Sector highlight */}
                    {sectorAreaD && (
                        <g>
                            <path d={sectorAreaD} fill="url(#sectorGrad)" />
                            <line x1={sectorStartX} y1={padding.top} x2={sectorStartX} y2={padding.top + chartH} stroke="#06b6d4" strokeWidth="1" strokeOpacity="0.6" />
                            <line x1={sectorEndX} y1={padding.top} x2={sectorEndX} y2={padding.top + chartH} stroke="#06b6d4" strokeWidth="1" strokeOpacity="0.6" />
                            <path d={sectorPathD} fill="none" stroke="#06b6d4" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                            <text x={(sectorStartX + sectorEndX) / 2} y={padding.top - 6} fill="#22d3ee" fontSize="10" fontWeight="bold" textAnchor="middle" letterSpacing="1">⚡ RACE SECTOR</text>
                        </g>
                    )}

                    {/* Axis Labels */}
                    <text x={padding.left - 8} y={padding.top + 10} fill="#9ca3af" fontSize="10" textAnchor="end">{stats.maxEle} ft</text>
                    <text x={padding.left - 8} y={padding.top + chartH} fill="#9ca3af" fontSize="10" textAnchor="end">{stats.minEle} ft</text>

                    <text x={padding.left} y={height - 8} fill="#9ca3af" fontSize="10" textAnchor="start">0 mi</text>
                    <text x={width - padding.right} y={height - 8} fill="#9ca3af" fontSize="10" textAnchor="end">{stats.totalDist} mi</text>

                    {/* Hover Guide Line & Tooltip point */}
                    {hoverPt && (
                        <g>
                            <line x1={hoverPt.x} y1={padding.top} x2={hoverPt.x} y2={padding.top + chartH} stroke="#22d3ee" strokeWidth="1.5" strokeDasharray="2 2" />
                            <circle cx={hoverPt.x} cy={hoverPt.y} r="5" fill="#06b6d4" stroke="#ffffff" strokeWidth="2" />
                        </g>
                    )}
                </svg>

                {/* Hover Tooltip Overlay */}
                {hoverPt && (
                    <div
                        className="absolute z-20 bg-gray-900/95 border border-cyan-500/50 p-2.5 rounded-lg shadow-xl text-xs space-y-1 pointer-events-none transition-all"
                        style={{
                            left: `${Math.min(80, Math.max(10, (hoverPt.x / width) * 100))}%`,
                            top: '10px'
                        }}
                    >
                        <div className="font-bold text-cyan-400">{hoverPt.pt.distanceMiles.toFixed(2)} mi</div>
                        {isHoverInSector && (
                            <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-wide">⚡ Race Sector</div>
                        )}
                        <div className="text-gray-200">Elev: <span className="font-semibold text-white">{Math.round(hoverPt.pt.elevationFt)} ft</span></div>
                        {currentGrade !== 0 && (
                            <div className="text-gray-300">Grade: <span className={`font-semibold ${currentGrade > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>{currentGrade > 0 ? `+${currentGrade}` : currentGrade}%</span></div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GroupRideElevationChart;
